import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, RefreshCw, Trash2, X, Zap, ZapOff, Eye, EyeOff, Star, StarOff } from "lucide-react";
import { api } from "@/lib/api";
import { inputCls } from "./shared";
import Originals from "./Originals";

const SUBTABS = [
  { id: "feed", label: "News Feed" },
  { id: "originals", label: "FORE Originals" },
  { id: "sources", label: "Sources" },
  { id: "categories", label: "Categories" },
  { id: "errors", label: "Errors" },
  { id: "settings", label: "Settings" },
  { id: "audit", label: "Audit Log" },
];

const mono = "font-mono text-[10px] uppercase tracking-[0.15em]";
const btn = "border border-white/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent disabled:opacity-50";
const dangerBtn = "border border-red-500/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-red-400 transition-colors duration-300 hover:bg-red-500/10 disabled:opacity-50";

const fmtTs = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const StatusBadge = ({ status }) => {
  const map = {
    ok: "bg-brand-accent/15 text-brand-accent",
    error: "bg-red-500/20 text-red-400",
    not_connected: "bg-white/10 text-white/50",
  };
  const labels = { ok: "Connected", error: "Error", not_connected: "Not Connected" };
  return <span className={`px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${map[status] || map.not_connected}`}>{labels[status] || status}</span>;
};

/* ---------------- News Feed ---------------- */
const NewsFeed = ({ auth, onAuthFail, categories }) => {
  const [data, setData] = useState({ items: [], total: 0, page: 1 });
  const [page, setPage] = useState(1);
  const [cat, setCat] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/news/admin/items", { ...auth, params: { page, limit: 30, category: cat || undefined } });
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, cat]);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, body, msg) => {
    try {
      await api.patch(`/news/admin/items/${id}`, body, auth);
      toast.success(msg);
      load();
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
      else toast.error(e?.response?.data?.detail || "Action failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this wire item permanently?")) return;
    try {
      await api.delete(`/news/admin/items/${id}`, auth);
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const breakingState = (it) => (it.breaking_override !== null && it.breaking_override !== undefined ? it.breaking_override : "auto");

  return (
    <div data-testid="news-feed">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select value={cat} onChange={(e) => { setCat(e.target.value); setPage(1); }} data-testid="news-feed-filter" className={`${inputCls} appearance-none`}>
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
        </select>
        <span className={`${mono} text-white/40`}>{data.total} items</span>
        <button onClick={load} className={btn} data-testid="news-feed-refresh">Refresh</button>
      </div>
      <div className="flex flex-col gap-3" data-testid="news-feed-list">
        {data.items.map((it) => (
          <div key={it.id} className={`border p-4 ${it.hidden ? "border-white/5 opacity-50" : "border-white/10"} bg-ink-2`} data-testid={`admin-wire-${it.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-display text-base font-bold uppercase leading-tight tracking-tight text-white">{it.headline}</p>
                <p className={`mt-2 ${mono} text-muted`}>
                  {it.source_name} · {it.category} · {fmtTs(it.published_at)}
                  {(it.breaking_override ?? it.is_breaking) ? " · BREAKING" : ""}
                  {it.hidden ? " · HIDDEN" : ""}
                  {it.featured ? " · FEATURED" : ""}
                </p>
                <a href={it.url} target="_blank" rel="noopener noreferrer" className={`mt-1 block truncate ${mono} text-white/35 hover:text-brand-accent`}>{it.url}</a>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <button onClick={() => patch(it.id, { featured: !it.featured }, it.featured ? "Unfeatured." : "Featured.")} className={btn} data-testid={`wire-feature-${it.id}`} title="Feature">
                  {it.featured ? <StarOff className="h-3.5 w-3.5" /> : <Star className="h-3.5 w-3.5" />}
                </button>
                <button
                  onClick={() => patch(it.id, { breaking_override: breakingState(it) === "auto" ? true : breakingState(it) === true ? false : null }, "Breaking updated.")}
                  className={btn}
                  data-testid={`wire-breaking-${it.id}`}
                  title={`Breaking: ${breakingState(it)} (auto → on → off)`}
                >
                  {breakingState(it) === true ? <ZapOff className="h-3.5 w-3.5" /> : <Zap className="h-3.5 w-3.5" />}
                </button>
                <button onClick={() => patch(it.id, { hidden: !it.hidden }, it.hidden ? "Visible again." : "Hidden.")} className={btn} data-testid={`wire-hide-${it.id}`} title="Hide/show">
                  {it.hidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                </button>
                <button onClick={() => remove(it.id)} className={dangerBtn} data-testid={`wire-delete-${it.id}`} title="Delete">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {data.items.length === 0 && <p className={`${mono} py-10 text-center text-white/40`}>No items yet — sources sync automatically.</p>}
      </div>
      <div className="mt-6 flex items-center gap-3">
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className={btn} data-testid="news-feed-prev">Prev</button>
        <span className={`${mono} text-white/40`}>Page {data.page}</span>
        <button disabled={data.items.length < 30} onClick={() => setPage((p) => p + 1)} className={btn} data-testid="news-feed-next">Next</button>
      </div>
    </div>
  );
};

/* ---------------- FORE Originals — full editorial manager in ./Originals.jsx ---------------- */

/* ---------------- Sources ---------------- */
const blankSource = { name: "", type: "publication", website: "", feed_url: "", default_category: "latest", integration_method: "official RSS", permission_status: "not_connected", notes: "", enabled: false };

const Sources = ({ auth, onAuthFail, categories }) => {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(blankSource);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/news/admin/sources", auth);
      setItems(res.data.sources);
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, body, msg) => {
    try {
      await api.patch(`/news/admin/sources/${id}`, body, auth);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    }
  };

  const retry = async (id) => {
    try {
      const res = await api.post(`/news/admin/sources/${id}/retry`, {}, auth);
      const r = res.data.result;
      if (r.ok) toast.success(`Synced: ${r.published} new, ${r.updated} updated, ${r.duplicates} dupes, ${r.rejected} rejected.`);
      else toast.error(`Failed: ${r.error}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this source? Its existing items stay published.")) return;
    await api.delete(`/news/admin/sources/${id}`, auth);
    toast.success("Source removed.");
    load();
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/news/admin/sources", form, auth);
      toast.success("Source added.");
      setModal(false);
      setForm(blankSource);
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not add source.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="news-sources">
      <div className="mb-4 flex items-center justify-between">
        <p className={`${mono} text-white/40`}>{items.filter((s) => s.status === "ok").length} connected · {items.filter((s) => s.status === "not_connected").length} not connected</p>
        <button onClick={() => setModal(true)} className="flex items-center gap-2 bg-white px-4 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink hover:bg-brand-accent" data-testid="source-new">
          <Plus className="h-3.5 w-3.5" /> Add Source
        </button>
      </div>
      <div className="flex flex-col gap-3" data-testid="sources-list">
        {items.map((s) => (
          <div key={s.id} className="border border-white/10 bg-ink-2 p-4" data-testid={`source-${s.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-display text-base font-bold uppercase tracking-tight text-white">{s.name}</p>
                  <StatusBadge status={s.status} />
                  {s.primary && <span className={`${mono} border border-white/30 px-2 py-0.5 text-white/60`}>Primary</span>}
                  {!s.enabled && s.status !== "not_connected" && <span className={`${mono} bg-white/10 px-2 py-0.5 text-white/50`}>Disabled</span>}
                </div>
                <p className={`mt-1.5 ${mono} text-muted`}>
                  {s.integration_method} · default: {s.default_category} · last success: {fmtTs(s.last_success_at)} · recent: {s.recent_count}
                </p>
                {s.last_error && <p className={`mt-1 ${mono} text-red-400`}>last error: {s.last_error}</p>}
                {s.feed_url && <p className={`mt-1 truncate ${mono} text-white/35`}>{s.feed_url}</p>}
                <p className={`mt-1 ${mono} text-white/35`}>fields displayed: {(s.permitted_fields || []).join(", ") || "none"}</p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                {s.status === "ok" && (
                  <button onClick={() => patch(s.id, { enabled: !s.enabled }, s.enabled ? "Source disabled." : "Source enabled.")} className={btn} data-testid={`source-toggle-${s.id}`}>
                    {s.enabled ? "Disable" : "Enable"}
                  </button>
                )}
                {s.enabled && <button onClick={() => retry(s.id)} className={btn} data-testid={`source-retry-${s.id}`}>Sync Now</button>}
                <button onClick={() => remove(s.id)} className={dangerBtn} data-testid={`source-delete-${s.id}`}>Remove</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[95] grid place-items-center bg-black/70 p-5 backdrop-blur-sm" onClick={() => setModal(false)} data-testid="source-modal">
          <form data-lenis-prevent onSubmit={add} onClick={(e) => e.stopPropagation()} className="max-h-[85vh] w-full max-w-xl overflow-y-auto overscroll-contain border border-white/15 bg-ink-2 p-7">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-accent">Add Approved Source</p>
              <button type="button" onClick={() => setModal(false)} className="text-white/50 hover:text-white" aria-label="Close"><X className="h-5 w-5" /></button>
            </div>
            <p className={`mt-3 ${mono} leading-relaxed text-white/40`}>
              Fail closed: a source only ingests when marked "Connected" — meaning you have identified and tested a permitted official feed/API.
            </p>
            <div className="mt-6 flex flex-col gap-4">
              <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Source name *" data-testid="source-field-name" className={`${inputCls} w-full`} />
              <input required value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} placeholder="Official website * (https://…)" data-testid="source-field-website" className={`${inputCls} w-full`} />
              <input value={form.feed_url} onChange={(e) => setForm((f) => ({ ...f, feed_url: e.target.value }))} placeholder="Feed / API endpoint (https://…)" data-testid="source-field-feed" className={`${inputCls} w-full`} />
              <div className="grid gap-4 sm:grid-cols-2">
                <select value={form.default_category} onChange={(e) => setForm((f) => ({ ...f, default_category: e.target.value }))} data-testid="source-field-category" className={`${inputCls} appearance-none`}>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                </select>
                <select value={form.permission_status} onChange={(e) => setForm((f) => ({ ...f, permission_status: e.target.value, enabled: e.target.value === "connected" ? f.enabled : false }))} data-testid="source-field-permission" className={`${inputCls} appearance-none`}>
                  <option value="not_connected">Not Connected (default)</option>
                  <option value="connected">Connected — tested &amp; permitted</option>
                </select>
              </div>
              <label className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted">
                <input type="checkbox" checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} disabled={form.permission_status !== "connected"} data-testid="source-field-enabled" className="h-4 w-4 accent-[#CCFF00]" />
                Enabled (requires Connected)
              </label>
              <textarea rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Notes on permitted fields / license" className={`${inputCls} w-full resize-none`} />
            </div>
            <div className="mt-7 flex gap-3">
              <button type="submit" disabled={busy} className="bg-brand-accent px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink hover:bg-white disabled:opacity-60" data-testid="source-save">
                {busy ? "Saving…" : "Add Source"}
              </button>
              <button type="button" onClick={() => setModal(false)} className={btn}>Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

/* ---------------- Categories ---------------- */
const Categories = ({ auth, onAuthFail, categories, reloadCategories }) => {
  const [label, setLabel] = useState("");
  const [keywords, setKeywords] = useState("");

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/news/admin/categories", { slug: label, label, keywords: keywords.split(",").map((k) => k.trim().toLowerCase()).filter(Boolean) }, auth);
      toast.success("Category added.");
      setLabel("");
      setKeywords("");
      reloadCategories();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not add.");
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete category "${slug}"? Items keep their last category label.`)) return;
    try {
      await api.delete(`/news/admin/categories/${slug}`, auth);
      toast.success("Deleted.");
      reloadCategories();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div data-testid="news-categories-admin">
      <div className="flex flex-col gap-2" data-testid="categories-list">
        {categories.map((c) => (
          <div key={c.slug} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-ink-2 p-4">
            <div>
              <p className="font-display text-base font-bold uppercase tracking-tight text-white">{c.label}</p>
              <p className={`mt-1 ${mono} text-muted`}>{c.slug}{(c.keywords || []).length ? ` · auto-match: ${c.keywords.slice(0, 6).join(", ")}${c.keywords.length > 6 ? "…" : ""}` : ""}</p>
            </div>
            {!["latest", "breaking"].includes(c.slug) && (
              <button onClick={() => remove(c.slug)} className={dangerBtn} data-testid={`category-delete-${c.slug}`}>Delete</button>
            )}
          </div>
        ))}
      </div>
      <form onSubmit={add} className="mt-6 flex flex-wrap gap-3" data-testid="category-add-form">
        <input required value={label} onChange={(e) => setLabel(e.target.value)} placeholder="New category name" data-testid="category-field-label" className={inputCls} />
        <input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="Auto-match keywords, comma separated" data-testid="category-field-keywords" className={`${inputCls} min-w-64 flex-1`} />
        <button type="submit" className="bg-brand-accent px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink hover:bg-white" data-testid="category-add">Add</button>
      </form>
    </div>
  );
};

/* ---------------- Errors ---------------- */
const Errors = ({ auth, onAuthFail }) => {
  const [data, setData] = useState({ errors: [], total: 0 });
  const load = useCallback(async () => {
    try {
      const res = await api.get("/news/admin/errors", auth);
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="news-errors">
      <p className={`mb-4 ${mono} text-white/40`}>{data.total} logged</p>
      <div className="flex flex-col gap-2" data-testid="errors-list">
        {data.errors.map((e) => (
          <div key={e.id} className="border border-red-500/20 bg-ink-2 p-4">
            <p className={`${mono} text-red-400`}>{e.error}</p>
            <p className={`mt-1 ${mono} text-muted`}>{e.source_name} · {fmtTs(e.ts)}</p>
            {e.detail && <p className={`mt-1 ${mono} break-all text-white/35`}>{e.detail}</p>}
          </div>
        ))}
        {data.errors.length === 0 && <p className={`${mono} py-10 text-center text-white/40`}>No errors logged — everything is syncing cleanly.</p>}
      </div>
    </div>
  );
};

/* ---------------- Settings ---------------- */
const Settings = ({ auth, onAuthFail }) => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/news/admin/settings", auth)
      .then((r) => setForm({
        refresh_minutes: r.data.settings.refresh_minutes,
        max_age_hours: r.data.settings.max_age_hours,
        breaking_keywords: (r.data.settings.breaking_keywords || []).join(", "),
        golf_keywords: (r.data.settings.golf_keywords || []).join(", "),
      }))
      .catch((e) => { if (e?.response?.status === 401) onAuthFail(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!form) return <p className={`${mono} text-white/40`}>Loading…</p>;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/news/admin/settings", {
        refresh_minutes: Number(form.refresh_minutes),
        max_age_hours: Number(form.max_age_hours),
        breaking_keywords: form.breaking_keywords.split(",").map((k) => k.trim()).filter(Boolean),
        golf_keywords: form.golf_keywords.split(",").map((k) => k.trim().toLowerCase()).filter(Boolean),
      }, auth);
      toast.success("Settings saved.");
    } catch {
      toast.error("Could not save settings.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="flex max-w-2xl flex-col gap-5" data-testid="news-settings">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={`mb-2 block ${mono} text-muted`}>Source check interval (minutes)</label>
          <input type="number" min="1" max="120" value={form.refresh_minutes} onChange={(e) => setForm((f) => ({ ...f, refresh_minutes: e.target.value }))} data-testid="settings-refresh" className={`${inputCls} w-full`} />
        </div>
        <div>
          <label className={`mb-2 block ${mono} text-muted`}>Max item age (hours)</label>
          <input type="number" min="1" max="336" value={form.max_age_hours} onChange={(e) => setForm((f) => ({ ...f, max_age_hours: e.target.value }))} data-testid="settings-maxage" className={`${inputCls} w-full`} />
        </div>
      </div>
      <div>
        <label className={`mb-2 block ${mono} text-muted`}>Breaking keywords (comma separated — conservative)</label>
        <textarea rows={3} value={form.breaking_keywords} onChange={(e) => setForm((f) => ({ ...f, breaking_keywords: e.target.value }))} data-testid="settings-breaking" className={`${inputCls} w-full resize-none`} />
      </div>
      <div>
        <label className={`mb-2 block ${mono} text-muted`}>Golf-relevance keywords (comma separated)</label>
        <textarea rows={5} value={form.golf_keywords} onChange={(e) => setForm((f) => ({ ...f, golf_keywords: e.target.value }))} data-testid="settings-golfkw" className={`${inputCls} w-full resize-none`} />
      </div>
      <button type="submit" disabled={busy} className="w-fit bg-brand-accent px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink hover:bg-white disabled:opacity-60" data-testid="settings-save">
        {busy ? "Saving…" : "Save Settings"}
      </button>
    </form>
  );
};

/* ---------------- Audit ---------------- */
const AuditLog = ({ auth, onAuthFail }) => {
  const [data, setData] = useState({ audit: [], total: 0, page: 1 });
  const [page, setPage] = useState(1);
  const load = useCallback(async () => {
    try {
      const res = await api.get("/news/admin/audit", { ...auth, params: { page } });
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="news-audit">
      <p className={`mb-4 ${mono} text-white/40`}>{data.total} audit records — every automated decision is logged</p>
      <div className="flex flex-col gap-2" data-testid="audit-list">
        {data.audit.map((a) => (
          <div key={a.id} className="border border-white/10 bg-ink-2 p-4">
            <p className={`${mono} ${a.action === "rejected" ? "text-red-400" : a.action === "updated" ? "text-brand-accent" : "text-white"}`}>
              {a.action?.toUpperCase()} · {a.source} · {fmtTs(a.ts)}
            </p>
            {a.url && <p className={`mt-1 truncate ${mono} text-white/40`}>{a.url}</p>}
            <p className={`mt-1 ${mono} text-muted`}>
              validation: {a.validation_result} · dedup: {a.duplicate_result} · fields shown: {(a.fields_displayed || []).join(", ") || "none"}
            </p>
          </div>
        ))}
        {data.audit.length === 0 && <p className={`${mono} py-10 text-center text-white/40`}>No audit records yet.</p>}
      </div>
      <div className="mt-6 flex items-center gap-3">
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className={btn}>Prev</button>
        <span className={`${mono} text-white/40`}>Page {data.page}</span>
        <button disabled={data.audit.length < 30} onClick={() => setPage((p) => p + 1)} className={btn}>Next</button>
      </div>
    </div>
  );
};

/* ---------------- Shell ---------------- */
export default function NewsAdmin({ token, onAuthFail }) {
  const [sub, setSub] = useState("feed");
  const [categories, setCategories] = useState([]);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadCategories = useCallback(async () => {
    try {
      const res = await api.get("/news/admin/categories", auth);
      setCategories(res.data.categories);
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  return (
    <div className="mt-8" data-testid="news-admin">
      <div className="flex flex-wrap gap-2" data-testid="news-subtabs">
        {SUBTABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setSub(t.id)}
            data-testid={`news-sub-${t.id}`}
            className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
              sub === t.id ? "border-brand-accent bg-brand-accent text-ink" : "border-white/15 text-muted hover:border-brand-accent hover:text-brand-accent"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="mt-8">
        {sub === "feed" && <NewsFeed auth={auth} onAuthFail={onAuthFail} categories={categories} />}
        {sub === "originals" && <Originals auth={auth} onAuthFail={onAuthFail} categories={categories} />}
        {sub === "sources" && <Sources auth={auth} onAuthFail={onAuthFail} categories={categories} />}
        {sub === "categories" && <Categories auth={auth} onAuthFail={onAuthFail} categories={categories} reloadCategories={loadCategories} />}
        {sub === "errors" && <Errors auth={auth} onAuthFail={onAuthFail} />}
        {sub === "settings" && <Settings auth={auth} onAuthFail={onAuthFail} />}
        {sub === "audit" && <AuditLog auth={auth} onAuthFail={onAuthFail} />}
      </div>
    </div>
  );
}
